import { useToast } from 'native-base';
import { createContext, JSX, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { SessionExpiryModal } from '../components/modals';
import { useTokenSync, useTokenTimeout } from '../hooks';
import { getRoute, useNavigate } from '../routes';

type TimeoutContextType = {
  /**
   * Sets the token expiry time and starts tracking
   */
  setTokenExpiryTime: (expiryTimestamp: number) => void;

  /**
   * Clears token expiry tracking
   */
  clearTokenExpiry: () => void;

  /**
   * Time in milliseconds until token expires (null if no token)
   */
  timeUntilExpiry: number | null;

  /**
   * Whether the token is about to expire (within warning threshold)
   */
  isTokenExpiringSoon: boolean;

  /**
   * Whether the token has expired
   */
  isTokenExpired: boolean;
};

const TimeoutContext = createContext<TimeoutContextType>({
  setTokenExpiryTime: () => {},
  clearTokenExpiry: () => {},
  timeUntilExpiry: null,
  isTokenExpiringSoon: false,
  isTokenExpired: false,
});

export const TimeoutConsumer = TimeoutContext.Consumer;

export const TimeoutProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const logout = useCallback(async (): Promise<void> => {
    navigate(getRoute('logout', {}));
  }, [navigate]);

  const handleTokenExpired = useCallback(async (): Promise<void> => {
    // Show toast notification to user
    toast.show({
      description: 'Your session has expired. You have been logged out for security reasons.',
      duration: 5000,
    });

    // Close the modal if it's open before logging out
    setShowExpiryModal(false);

    await logout();
  }, [logout, toast]);

  const handleTokenExpiringSoon = useCallback(() => {
    setShowExpiryModal(true);
  }, []);

  const {
    timeUntilExpiry,
    isTokenExpiringSoon,
    isTokenExpired,
    storedExpiryTimestamp,
    setTokenExpiryTime,
    clearTokenExpiry,
  } = useTokenTimeout({
    onTokenExpired: handleTokenExpired,
    onTokenExpiringSoon: handleTokenExpiringSoon,
  });

  // Handle cross-tab synchronization for web
  useTokenSync({
    storedExpiryTimestamp,
    onTokenChange: setTokenExpiryTime,
    onTokenExpired: handleTokenExpired,
  });

  // Close modal when token tracking stops (no stored timestamp)
  useEffect(() => {
    if (storedExpiryTimestamp == null && showExpiryModal) {
      setShowExpiryModal(false);
    }
  }, [storedExpiryTimestamp, showExpiryModal]);

  // Cleanup: close modal on unmount
  useEffect(() => {
    return () => {
      if (showExpiryModal) {
        setShowExpiryModal(false);
      }
    };
  }, [showExpiryModal]);

  const providerValue: TimeoutContextType = {
    setTokenExpiryTime,
    clearTokenExpiry,
    timeUntilExpiry,
    isTokenExpiringSoon,
    isTokenExpired,
  };

  const handleLogoutNow = useCallback(async () => {
    setShowExpiryModal(false);
    await logout();
  }, [logout]);

  return (
    <>
      <TimeoutContext.Provider value={providerValue}>{children}</TimeoutContext.Provider>

      <SessionExpiryModal
        isOpen={showExpiryModal}
        onClose={() => setShowExpiryModal(false)}
        onLogoutNow={handleLogoutNow}
      />
    </>
  );
};

export const useTimeoutContext = (): TimeoutContextType => {
  return useContext(TimeoutContext);
};
